/**
 * /join + /join/success DOM Rendering.
 *
 * WHAT THIS FILE DOES:
 * - Renders every visual state of #join-root: loading, signed-out, unpaid,
 *   already-member, success polling/confirmed/stuck, error.
 * - Owns the tiny DOM helpers (createElement, Google/check SVGs) and the
 *   hero/pricing/identity building blocks those states share.
 *
 * WHY THIS EXISTS:
 * - Extracted verbatim from src/client/join.ts so the controller stays under
 *   the god-module line. No behavior changes; identical copy strings.
 *
 * DESIGN NOTE:
 * - Every renderer takes the JoinController instance and calls back through
 *   its members (`controller.handleCheckout()`, `controller.renderStatus()`,
 *   ...) so the controller keeps owning state and network actions.
 *
 * CALLED BY:
 * - src/client/join.ts (JoinController.render() dispatcher)
 *
 * @file src/client/join-render.ts
 */

import { looksLikeBetaCohort } from './membership-cohort.js';
import { resolveTrialAccessCopy } from './membership-trial-state.js';
import { URL_PREFIX_CHECKOUT_SOURCE } from '../lib/url-prefix-continuation.js';
import { resolveUrlPrefixJoinHeroCopy } from './url-prefix-join.js';
import type { JoinController } from './join.js';

export const CHROME_WEB_STORE_URL =
  'https://chromewebstore.google.com/detail/Gist%20-%20Anti-Slop%20Filter%20for%20the%20Internet/iilleobakpmdhineleplnjifbpoiioga';

export function getHelpUrl(): string {
  const pub = window.__GIST_JOIN_PUBLIC__;
  if (typeof pub?.helpUrl === 'string' && /^https?:\/\//.test(pub.helpUrl)) {
    return pub.helpUrl;
  }
  // Contact-support fallback: this is called from "Email us" /
  // "We're here to help" affordances. The contact form lives at /help.
  return `${window.location.origin}/help`;
}

/* ────────────────────────────────────────────────────────────────────────
   Tiny DOM helpers — no framework, no abstraction we will not reuse.
   ──────────────────────────────────────────────────────────────────────── */

export function createElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number | boolean> = {},
  ...children: Array<Node | string>
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === false || value === null || value === undefined) continue;
    if (value === true) {
      node.setAttribute(key, '');
    } else {
      node.setAttribute(key, String(value));
    }
  }
  for (const child of children) {
    node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
  }
  return node;
}

export function googleSvg(): SVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 18 18');
  svg.setAttribute('width', '18');
  svg.setAttribute('height', '18');
  svg.setAttribute('aria-hidden', 'true');
  const paths = [
    { d: 'M17.64 9.2045c0-.6382-.0573-1.2527-.1636-1.8409H9v3.4818h4.84a4.1371 4.1371 0 0 1-1.7954 2.7173v2.2618h2.9082c1.7027-1.5682 2.6872-3.8791 2.6872-6.6201z', fill: '#4285F4' },
    { d: 'M9 18c2.43 0 4.4673-.8063 5.9564-2.1764l-2.9082-2.2618c-.8063.54-1.8372.8582-3.0482.8582-2.3446 0-4.329-1.5845-5.0327-3.7109H.9573v2.3318C2.4382 15.9836 5.4818 18 9 18z', fill: '#34A853' },
    { d: 'M3.9673 10.7091A5.4009 5.4009 0 0 1 3.6873 9a5.4009 5.4009 0 0 1 .28-1.7091V4.9591H.9573A8.9964 8.9964 0 0 0 0 9c0 1.4573.3482 2.8363.9573 4.0409l3.0109-2.3318z', fill: '#FBBC05' },
    { d: 'M9 3.5791c1.3218 0 2.5109.4545 3.4418 1.3454l2.5818-2.5818C13.4636.9009 11.4264 0 9 0 5.4818 0 2.4382 2.0164.9573 4.9591l3.0109 2.3318C4.671 5.1636 6.6554 3.5791 9 3.5791z', fill: '#EA4335' }
  ];
  for (const { d, fill } of paths) {
    const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    p.setAttribute('d', d);
    p.setAttribute('fill', fill);
    svg.appendChild(p);
  }
  return svg;
}

export function checkSvg(): SVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M5 12l4 4L19 7');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  svg.appendChild(path);
  return svg;
}

/* ────────────────────────────────────────────────────────────────────────
   State renderers — single owner of #join-root content.
   ──────────────────────────────────────────────────────────────────────── */

export function renderError(controller: JoinController): void {
  const hero = renderHero(controller, { kind: 'error' });
  const actions = createElement('div', { class: 'join-actions' });
  const retry = createElement(
    'button',
    { type: 'button', class: 'join-btn join-btn--primary' },
    'Try again'
  );
  retry.addEventListener('click', () => {
    window.location.reload();
  });
  actions.appendChild(retry);

  const fineprint = createElement(
    'p',
    { class: 'join-fineprint' },
    'Still stuck? ',
    linkOut('Email us', getHelpUrl())
  );

  controller.root.replaceChildren(hero, actions, controller.renderStatus(), fineprint);
}

export function renderLoading(controller: JoinController): void {
  const dot = createElement('span', { class: 'join-loading-dot', 'aria-hidden': 'true' });
  const wrap = createElement('div', { class: 'join-loading', role: 'status', 'aria-label': 'Loading' }, dot);
  controller.root.replaceChildren(wrap);
}

export function renderSignedOut(controller: JoinController): void {
  const hero = renderHero(controller, { kind: 'signed-out' });
  const sourceContext = renderUrlPrefixContext(controller);
  const pricing = renderPricing(controller);
  const actions = createElement('div', { class: 'join-actions' });

  const googleBtn = createElement(
    'button',
    {
      type: 'button',
      class: 'join-btn join-btn--primary' + (controller.busy ? ' join-btn--loading' : ''),
      disabled: controller.busy
    },
    googleSvg(),
    'Continue with Google'
  );
  googleBtn.addEventListener('click', () => {
    void controller.handleGoogleSignIn();
  });
  actions.appendChild(googleBtn);

  const fineprint = createElement(
    'p',
    { class: 'join-fineprint' },
    'Google sign-in. Cancel any time. ',
    linkOut('Help', getHelpUrl())
  );

  controller.root.replaceChildren(...withOptionalSourceContext(
    [hero, pricing, actions, controller.renderStatus(), fineprint],
    sourceContext
  ));
}

export function renderUnpaid(controller: JoinController): void {
  // Beta-cohort copy is profile-driven (createdAt + isBeta), not user-instance-
  // driven, so it applies equally to Firebase users and bridge-only sessions.
  const isBeta = controller.hasIdentity()
    ? looksLikeBetaCohort(
      controller.profileMeta,
      controller.billing,
      window.__GIST_JOIN_PUBLIC__?.betaCohortCutoffIso
    )
    : false;
  const hero = renderHero(controller, { kind: isBeta ? 'beta-ended' : 'signed-in' });
  const sourceContext = renderUrlPrefixContext(controller);
  const pricing = renderPricing(controller);
  const identity = renderIdentity(controller);
  const actions = createElement('div', { class: 'join-actions' });

  const ctaLabel = 'Continue to checkout';

  const subscribeBtn = createElement(
    'button',
    {
      type: 'button',
      class: 'join-btn join-btn--primary' + (controller.busy ? ' join-btn--loading' : ''),
      disabled: controller.busy
    },
    ctaLabel
  );
  subscribeBtn.addEventListener('click', () => {
    void controller.handleCheckout();
  });
  actions.appendChild(subscribeBtn);

  const fineprint = createElement(
    'p',
    { class: 'join-fineprint' },
    'Eligible new members need no card for 21 days. Returning members pay at checkout. ',
    linkOut('Help', getHelpUrl())
  );

  controller.root.replaceChildren(...withOptionalSourceContext(
    [hero, pricing, identity, actions, controller.renderStatus(), fineprint],
    sourceContext
  ));
}

export function renderAlreadyMember(controller: JoinController): void {
  if (controller.bootstrap.source === URL_PREFIX_CHECKOUT_SOURCE) {
    continueToUrlPrefix(controller);
    return;
  }

  const hero = renderHero(controller, { kind: 'paid' });
  const identity = renderIdentity(controller);
  const actions = createElement('div', { class: 'join-actions' });

  const installBtn = createElement(
    'a',
    {
      href: CHROME_WEB_STORE_URL,
      target: '_blank',
      rel: 'noopener noreferrer',
      class: 'join-btn join-btn--primary'
    },
    'Install the Chrome extension'
  );
  actions.appendChild(installBtn);

  const manageBtn = createElement(
    'button',
    {
      type: 'button',
      class: 'join-btn join-btn--ghost' + (controller.busy ? ' join-btn--loading' : ''),
      disabled: controller.busy
    },
    'Billing & receipts'
  );
  manageBtn.addEventListener('click', () => {
    void controller.handleManage();
  });
  actions.appendChild(manageBtn);

  const fineprint = createElement(
    'p',
    { class: 'join-fineprint' },
    'Need anything? ',
    linkOut('We’re here to help', getHelpUrl())
  );

  controller.root.replaceChildren(hero, identity, actions, controller.renderStatus(), fineprint);
}

/**
 * Renders the post-Checkout confirmation page (used on /join/success).
 *
 * Polls /api/auth/session until `subscriptionActive` is true. The poll
 * is bounded; once we exhaust attempts the page surfaces a manual
 * refresh CTA so the user is never stuck.
 */
export function renderSuccess(controller: JoinController): void {
  if (!controller.auth) {
    renderLoading(controller);
    return;
  }
  if (!controller.hasIdentity()) {
    const hero = renderHero(controller, { kind: 'success-resign' });
    const actions = createElement('div', { class: 'join-actions' });
    const signIn = createElement(
      'button',
      {
        type: 'button',
        class: 'join-btn join-btn--primary' + (controller.busy ? ' join-btn--loading' : ''),
        disabled: controller.busy
      },
      googleSvg(),
      'Sign back in with Google'
    );
    signIn.addEventListener('click', () => {
      void controller.handleGoogleSignIn();
    });
    actions.appendChild(signIn);
    controller.root.replaceChildren(hero, actions, controller.renderStatus());
    return;
  }

  if (controller.hasPaidEntitlement()) {
    renderSuccessConfirmed(controller);
    return;
  }

  // Pending: kick off polling.
  if (controller.successPollTimer === null) {
    controller.startSuccessPoll();
  }
  const hero = renderHero(controller, { kind: 'success-pending' });
  const status = createElement(
    'p',
    { class: 'join-status join-status--info' },
    'Confirming your access…'
  );
  controller.root.replaceChildren(hero, status);
}

export function renderSuccessConfirmed(controller: JoinController): void {
  if (controller.bootstrap.source === URL_PREFIX_CHECKOUT_SOURCE) {
    continueToUrlPrefix(controller);
    return;
  }

  const hero = renderHero(controller, { kind: 'success-confirmed' });
  const actions = createElement('div', { class: 'join-actions' });

  if (controller.bootstrap.source === 'extension') {
    const open = createElement(
      'button',
      { type: 'button', class: 'join-btn join-btn--primary' },
      'Close this tab'
    );
    open.addEventListener('click', () => {
      window.close();
    });
    actions.appendChild(open);

    const close = createElement(
      'p',
      { class: 'join-fineprint' },
      'Then open any page and click the Gist icon in your toolbar.'
    );
    controller.root.replaceChildren(hero, actions, close);
    return;
  }

  const install = createElement(
    'a',
    {
      href: CHROME_WEB_STORE_URL,
      target: '_blank',
      rel: 'noopener noreferrer',
      class: 'join-btn join-btn--primary'
    },
    'Install the Chrome extension'
  );
  actions.appendChild(install);

  const manageBtn = createElement(
    'button',
    {
      type: 'button',
      class: 'join-btn join-btn--ghost' + (controller.busy ? ' join-btn--loading' : ''),
      disabled: controller.busy
    },
    'Billing & receipts'
  );
  manageBtn.addEventListener('click', () => {
    void controller.handleManage();
  });
  actions.appendChild(manageBtn);

  const fineprint = createElement(
    'p',
    { class: 'join-fineprint' },
    'Pin the extension to your toolbar after install. ',
    linkOut('Help', getHelpUrl())
  );

  controller.root.replaceChildren(hero, actions, fineprint);
}

/**
 * Surfaced when polling exhausts. The webhook will land eventually; the
 * user just needs to refresh once it does. Manual refresh button beats
 * polling forever.
 */
export function renderSuccessStillPending(controller: JoinController): void {
  const hero = renderHero(controller, { kind: 'success-stuck' });
  const actions = createElement('div', { class: 'join-actions' });
  const refresh = createElement(
    'button',
    { type: 'button', class: 'join-btn join-btn--primary' },
    'Check again'
  );
  refresh.addEventListener('click', () => {
    controller.successPollAttempts = 0;
    void controller.refreshSessionAndBilling().then(() => controller.render());
  });
  actions.appendChild(refresh);

  const fineprint = createElement(
    'p',
    { class: 'join-fineprint' },
    'Access confirmation sometimes runs a little long. ',
    linkOut('Email us if it doesn’t land', getHelpUrl())
  );
  controller.root.replaceChildren(hero, actions, fineprint);
}

/* ────────────────────────────────────────────────────────────────────────
   Hero / shared blocks
   ──────────────────────────────────────────────────────────────────────── */

export function renderHero(controller: JoinController, args: {
  kind:
    | 'signed-out'
    | 'signed-in'
    | 'beta-ended'
    | 'paid'
    | 'success-pending'
    | 'success-confirmed'
    | 'success-resign'
    | 'success-stuck'
    | 'error';
}): HTMLElement {
  const block = createElement('section', { class: 'join-hero' });

  let eyebrow: string | null = null;
  let eyebrowAlert = false;
  let headlineText = 'Become a member.';
  let headlineMark: string | null = null;
  let sub = '';

  switch (args.kind) {
    case 'signed-out':
      {
        const urlPrefixCopy = resolveUrlPrefixJoinHeroCopy('signed-out', controller.resolveUrlPrefixContext());
        if (urlPrefixCopy) {
          eyebrow = urlPrefixCopy.eyebrow ?? null;
          headlineText = urlPrefixCopy.headlineText;
          sub = urlPrefixCopy.sub;
        } else {
          headlineText = 'Nine dollars a month.';
          headlineMark = 'Every page you read.';
          sub = '';
        }
      }
      break;
    case 'signed-in':
      {
        const urlPrefixCopy = resolveUrlPrefixJoinHeroCopy('signed-in', controller.resolveUrlPrefixContext());
        if (urlPrefixCopy) {
          eyebrow = urlPrefixCopy.eyebrow ?? null;
          headlineText = urlPrefixCopy.headlineText;
          sub = urlPrefixCopy.sub;
        } else {
          headlineText = 'Pick your plan.';
          sub = '';
        }
      }
      break;
    case 'beta-ended':
      eyebrow = 'Beta is over';
      eyebrowAlert = true;
      headlineText = 'Keep your seat.';
      sub =
        'Thanks for testing Gist during the beta. Same product, same login — $9 a month or $99 a year.';
      break;
    case 'paid':
      {
        const urlPrefixCopy = resolveUrlPrefixJoinHeroCopy('paid', controller.resolveUrlPrefixContext());
        eyebrow = "You're in";
        if (urlPrefixCopy) {
          headlineText = urlPrefixCopy.headlineText;
          sub = urlPrefixCopy.sub;
        } else {
          const trialCopy = resolveTrialAccessCopy(
            controller.billing?.subscriptionStatus,
            controller.billing?.subscriptionTrialEnd,
            false
          );
          headlineText = trialCopy?.headline ?? 'Welcome to Gist.';
          sub = trialCopy
            ? `${trialCopy.detail} Cancel any time.`
            : 'Install the extension below and pin it once. Every page you read from now on is a gist away.';
        }
      }
      break;
    case 'success-pending':
      eyebrow = 'Almost there';
      headlineText = 'Confirming your access.';
      sub = 'This usually takes a second.';
      break;
    case 'success-confirmed':
      {
        const urlPrefixCopy = resolveUrlPrefixJoinHeroCopy('success-confirmed', controller.resolveUrlPrefixContext());
        if (urlPrefixCopy) {
          headlineText = urlPrefixCopy.headlineText;
          sub = urlPrefixCopy.sub;
        } else if (controller.bootstrap.source === 'extension') {
          const trialCopy = resolveTrialAccessCopy(
            controller.billing?.subscriptionStatus,
            controller.billing?.subscriptionTrialEnd,
            true
          );
          headlineText = trialCopy?.headline ?? 'Welcome to Gist.';
          sub = trialCopy
            ? trialCopy.detail
            : 'Your extension is ready. Close this tab and open any page.';
        } else {
          const trialCopy = resolveTrialAccessCopy(
            controller.billing?.subscriptionStatus,
            controller.billing?.subscriptionTrialEnd,
            false
          );
          headlineText = trialCopy?.headline ?? 'Welcome to Gist.';
          sub = trialCopy
            ? trialCopy.detail
            : 'Install the extension below and pin it once. Every page you read from now on is a gist away.';
        }
      }
      break;
    case 'success-resign':
      eyebrow = 'One more step';
      headlineText = 'Sign back in to confirm.';
      sub = 'Checkout is complete. Sign in with the same Google account to finish setup.';
      break;
    case 'success-stuck':
      eyebrow = 'Still confirming';
      headlineText = 'This is taking longer than usual.';
      sub = "Try again in a moment. If it still won't confirm, email us — we'll sort it out by hand.";
      break;
    case 'error':
      headlineText = 'Something went wrong.';
      sub = 'Please try again. If it keeps failing, we’d like to know.';
      break;
  }

  if (args.kind === 'success-confirmed') {
    const mark = createElement('div', { class: 'join-success-mark' }, checkSvg());
    block.appendChild(mark);
  }

  if (eyebrow) {
    block.appendChild(
      createElement(
        'p',
        { class: 'join-eyebrow' + (eyebrowAlert ? ' join-eyebrow--alert' : '') },
        eyebrow
      )
    );
  }

  const headline = createElement('h1', { class: 'join-headline' }, headlineText);
  if (headlineMark) {
    headline.appendChild(document.createTextNode(' '));
    headline.appendChild(createElement('span', { class: 'join-headline-mark' }, headlineMark));
  }
  block.appendChild(headline);

  if (sub) {
    block.appendChild(createElement('p', { class: 'join-sub' }, sub));
  }
  return block;
}

export function renderPricing(controller: JoinController): HTMLElement {
  const wrap = createElement('div', { class: 'join-pricing' });
  const toggle = createElement('div', {
    class: 'join-price-toggle',
    role: 'group',
    'aria-label': 'Choose a billing plan'
  });

  const monthBtn = createElement(
    'button',
    {
      type: 'button',
      class: 'join-price-option',
      'aria-pressed': controller.currentInterval === 'month' ? 'true' : 'false'
    },
    renderPriceAmount('$9', '/month'),
    createElement(
      'span',
      { class: 'join-price-note' },
      '21 day free trial'
    )
  );
  monthBtn.addEventListener('click', () => {
    if (controller.currentInterval === 'month') return;
    controller.currentInterval = 'month';
    controller.render();
  });

  const yearBtn = createElement(
    'button',
    {
      type: 'button',
      class: 'join-price-option',
      'aria-pressed': controller.currentInterval === 'year' ? 'true' : 'false'
    },
    renderPriceAmount('$99', '/year'),
    createElement(
      'span',
      { class: 'join-price-note join-price-note--save' },
      '21 day free trial'
    )
  );
  yearBtn.addEventListener('click', () => {
    if (controller.currentInterval === 'year') return;
    controller.currentInterval = 'year';
    controller.render();
  });

  toggle.appendChild(monthBtn);
  toggle.appendChild(yearBtn);
  wrap.appendChild(toggle);

  wrap.appendChild(
    createElement(
      'p',
      { class: 'join-price-fineprint' },
      'No card for eligible trials. Switch plans any time.'
    )
  );

  return wrap;
}

export function renderPriceAmount(amount: string, period: string): HTMLElement {
  return createElement(
    'span',
    { class: 'join-price-amount' },
    amount,
    createElement('span', {}, period)
  );
}

export function renderIdentity(controller: JoinController): HTMLElement {
  const email = controller.getDisplayEmail() ?? 'Signed in';
  const wrap = createElement('div', { class: 'join-identity' });
  const info = createElement('div', { class: 'join-identity-info' });
  info.appendChild(createElement('span', { class: 'join-identity-label' }, 'Signed in as'));
  info.appendChild(createElement('span', { class: 'join-identity-email' }, email));

  const switchBtn = createElement(
    'button',
    { type: 'button', class: 'join-identity-action' },
    'Switch account'
  );
  switchBtn.addEventListener('click', () => {
    void controller.handleSignOut();
  });

  wrap.appendChild(info);
  wrap.appendChild(switchBtn);
  return wrap;
}

export function renderUrlPrefixContext(controller: JoinController): HTMLElement | null {
  const context = controller.resolveUrlPrefixContext();
  if (!context) {
    return null;
  }
  return createElement(
    'div',
    { class: 'join-source-context', 'aria-label': 'Saved URL' },
    createElement('span', { class: 'join-source-label' }, 'Gisting'),
    createElement('span', { class: 'join-source-url' }, context.label)
  );
}

export function withOptionalSourceContext(nodes: HTMLElement[], sourceContext: HTMLElement | null): HTMLElement[] {
  if (!sourceContext) {
    return nodes;
  }
  const [hero, ...rest] = nodes;
  return [hero, sourceContext, ...rest];
}

export function linkOut(label: string, href: string): HTMLElement {
  return createElement(
    'a',
    { href, target: '_blank', rel: 'noopener noreferrer' },
    label
  );
}

export function continueToUrlPrefix(controller: JoinController): void {
  const path = controller.resolveContinuePath();
  if (!path) {
    controller.bootstrap.source = 'web';
    controller.render();
    return;
  }
  const hero = renderHero(controller, { kind: controller.bootstrap.mode === 'success' ? 'success-confirmed' : 'paid' });
  const status = createElement(
    'p',
    { class: 'join-status join-status--success' },
    'Opening your gist…'
  );
  const actions = createElement('div', { class: 'join-actions' });
  const link = createElement(
    'a',
    { href: path, class: 'join-btn join-btn--primary' },
    'Continue gisting'
  );
  actions.appendChild(link);
  controller.root.replaceChildren(hero, status, actions);
  if (controller.continuationStarted) {
    return;
  }
  controller.continuationStarted = true;
  window.setTimeout(() => {
    window.location.assign(path);
  }, 250);
}
